import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { X } from "@phosphor-icons/react";

import { Button } from "./ui/button";
import { cn } from "../lib/utils";

/**
 * Measures how much viewport is left below the element and reports that as its height, so a
 * master/detail pane ends at the fold on any resolution instead of at a hard-coded height.
 *
 * Below the desktop breakpoint the panes stack, so the element is left to flow with the page.
 */
const SPLIT_BREAKPOINT = 1024;

export function useRemainingViewportHeight(gutter = 20) {
  const ref = useRef(null);
  const [height, setHeight] = useState(null);
  const measure = useCallback(() => {
    const node = ref.current;
    if (!node) return;
    if (window.innerWidth < SPLIT_BREAKPOINT) {
      setHeight(null);
      return;
    }
    const available = Math.max(360, Math.round(window.innerHeight - node.getBoundingClientRect().top - gutter));
    setHeight((current) => (current == null || Math.abs(current - available) > 2 ? available : current));
  }, [gutter]);
  // Re-measured after every render so content appearing above never leaves the pane overflowing the
  // fold, and on resize so the same layout holds at any window size.
  useLayoutEffect(measure);
  useEffect(() => {
    window.addEventListener("resize", measure);
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    observer?.observe(document.documentElement);
    return () => { window.removeEventListener("resize", measure); observer?.disconnect(); };
  }, [measure]);
  return [ref, height];
}

/**
 * The house master/detail shell: a list that keeps the full width until something is selected, then
 * yields to a persistent in-flow detail pane beside it.
 *
 * Deliberately not an overlay. A modal or a drawer hides the list it came from, so an operator
 * cannot compare one record against the others, loses their place on dismiss, and on a wide screen
 * reads a narrow column with the page greyed out behind it.
 */
export default function MasterDetailPane({
  master,
  detail,
  open = false,
  onClose,
  title,
  masterWidth = "minmax(320px,0.8fr)",
  detailWidth = "minmax(420px,1.2fr)",
  className,
  detailClassName,
  "data-testid": testId,
}) {
  const [ref, height] = useRemainingViewportHeight();
  return (
    <div
      ref={ref}
      data-testid={testId}
      // The width ratio rides on custom properties because a dynamic class name is invisible to
      // Tailwind at build time and would be dropped from the stylesheet. They must be set here, on
      // the grid that reads them: custom properties inherit down, not up.
      style={{
        ...(height ? { height } : {}),
        ...(open ? { "--master-width": masterWidth, "--detail-width": detailWidth } : {}),
      }}
      className={cn(
        "grid min-h-[360px] overflow-hidden rounded-xl border border-border bg-card",
        !height && "lg:h-[62vh]",
        open && "lg:grid-cols-[var(--master-width)_var(--detail-width)]",
        className,
      )}
      // Passed as custom properties so a caller can set the ratio without a dynamic class name,
      // which Tailwind cannot see at build time and would silently drop.
      {...(open ? { "data-detail-open": "true" } : {})}
    >
      <div className={cn("min-h-0 overflow-auto", open && "border-b border-border lg:border-b-0 lg:border-r")}>
        {master}
      </div>
      {open && (
        <div className={cn("flex min-h-0 flex-col overflow-hidden", detailClassName)}>
          {(title || onClose) && (
            <div className="flex h-9 shrink-0 items-center justify-between gap-2 border-b border-border bg-muted/30 px-2.5">
              <span className="truncate text-xs font-semibold text-foreground">{title}</span>
              {onClose && (
                <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={onClose} aria-label="Close details">
                  <X size={13} />
                </Button>
              )}
            </div>
          )}
          <div className="min-h-0 flex-1 overflow-auto">{detail}</div>
        </div>
      )}
    </div>
  );
}
